"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../css/frontShoplogin.module.css";
import { MdBackspace } from "react-icons/md";
import { frontshopLogin } from "../../../../api/userManagement/userManagementApi";
import { useAuth } from "../../../../context/AuthContext";

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

export default function FrontShopLogin({ expectedCode = "1234" }) {
  const navigate = useNavigate();
  const { frontshopLogin: authFrontshopLogin } = useAuth();
  const codeLength = useMemo(() => expectedCode.length || 4, [expectedCode]);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle"); // idle | success | error

  useEffect(() => {
    if (code.length === codeLength) {
      const isOverride = code === expectedCode; // e.g. 1234 while rolling out
      
      handleFrontshopLogin(code);
    }
  }, [code, codeLength, expectedCode, navigate]);

  const handleFrontshopLogin = async (frontshopcode) => {
    try {
      const response = await frontshopLogin(frontshopcode);
      if (response.token && response.user) {
        // Store user in AuthContext
        authFrontshopLogin(response.user, response.token);
        
        const t = setTimeout(() => {
          navigate("/frontshop");
        }, 500);

        return () => clearTimeout(t);
      } else {
        setStatus("error");
        const t = setTimeout(() => {
          setCode("");
          setStatus("idle");
        }, 800);
        return () => clearTimeout(t);
      }
    } catch (error) {
      console.error("Frontshop login failed:", error);
      setStatus("error");
      const t = setTimeout(() => {
        setCode("");
        setStatus("idle");
      }, 800);
      return () => clearTimeout(t);
    }
  };

  const press = (d) => {
    if (status !== "idle") return;
    if (code.length < codeLength) setCode((c) => c + d);
  };

  const backspace = () => {
    if (status !== "idle") return;
    setCode((c) => c.slice(0, -1));
  };

  const clear = () => {
    if (status !== "idle") return setStatus("idle");
    setCode("");
  };

  return (
    <div className={styles.landing}>
      {/* Home button */}
      <button className={styles.homeButton} onClick={() => navigate("/")} aria-label="Home">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9,22 9,12 15,12 15,22"></polyline>
        </svg>
      </button>

      {/* Main container with logo and keypad */}
      <div className={styles.mainContainer}>
        {/* Logo and title in same line */}
        <div className={styles.logoGroup}>
          <div className={styles.logo}>
            <img
              src="/images/logo.png"
              alt="MediIntel Logo"
              width={100}
              height={100}
              style={{ width: '100px', height: '100px' }}
            />
          </div>
          <h1 className={styles.logoText}>MediIntel</h1>
        </div>

        {/* Keypad with original styling */}
        <div className={styles.panel}>
          <div className={styles.dots} aria-label="code progress">
            {Array.from({ length: codeLength }).map((_, i) => (
              <div key={i} className={i < code.length ? styles.dotFilled : styles.dot} />
            ))}
          </div>

          <div className={styles.keypad}>
            {DIGITS.slice(0, 3).map((d) => (
              <button key={d} className={styles.key} onClick={() => press(d)}>{d}</button>
            ))}
            {DIGITS.slice(3, 6).map((d) => (
              <button key={d} className={styles.key} onClick={() => press(d)}>{d}</button>
            ))}
            {DIGITS.slice(6, 9).map((d) => (
              <button key={d} className={styles.key} onClick={() => press(d)}>{d}</button>
            ))}
            <div className={styles.keypadSpacer}></div>
            <button className={styles.key} onClick={() => press("0")}>0</button>
            <div className={styles.keypadSpacer}></div>
          </div>

          <div className={styles.actions}>
            <button className={styles.actionBtn} onClick={clear}>Clear</button>
            <button className={styles.actionBtn} onClick={backspace} title="Backspace" aria-label="Backspace">
              <MdBackspace size={20} color="#ef4444" />
            </button>
          </div>

          {status === "success" && <div className={styles.success}>Access granted</div>}
          {status === "error" && <div className={styles.error}>Incorrect code</div>}
        </div>
      </div>
    </div>
  );
}